import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { QuillEditorComponent, QuillModules } from "ngx-quill";
import Quill from "quill";
import "quill-mention";
import { fromEvent, take, takeUntil } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { CommentTargetType } from "@gtmhub/comments";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { SearchService } from "@gtmhub/search/services/search.service";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { getCurrentUserId } from "@gtmhub/users";
import { Assignee, UnknownAssignee, UserAssignee } from "@webapp/assignees/models/assignee.models";
import { assigneeFromRedux } from "@webapp/assignees/utils/assignee.utils";
import { Comment, NewComment } from "@webapp/comments/models/comments.models";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { localize } from "@webapp/localization/utils/localization.utils";
import { ISearchCondition, ISearchParams, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";
import { IGif, SizeOfGif } from "@webapp/shared/rich-text-editor/add-gif/add-gif.models";
import { MentionBlot } from "@webapp/shared/rich-text-editor/blots/mention.blot";
import { MentionAttachedEvent, MentionDeletedEvent } from "@webapp/shared/rich-text-editor/events/mention-deleted.event";
import { deltaToMarkdownConverters } from "@webapp/shared/rich-text-editor/markdown-converter/from-delta.converters";
import { MarkedJsService } from "@webapp/shared/rich-text-editor/marked-js/marked-js.service";
import { RichTextEditorIconEnum } from "@webapp/shared/rich-text-editor/rich-text-editor-icon/rich-text-editor-icon.enum";
import { EmbeddedEmoji } from "./blots/embedded-emoji.blot";
import { deltaToMarkdown } from "./markdown-converter/markdown-converter";
import { WebappQuillTheme } from "./rich-text-editor.theme";
import { convertMarkdownToRichText, isGifOrEmojiPickerSearchFieldSelected, searchItemToUserAssignee, stripHtmlElements } from "./rich-text-editor.utils";

Quill.register("themes/ghQuillTheme", WebappQuillTheme, true);
Quill.register(MentionBlot, true);
Quill.register(EmbeddedEmoji, true);

const Delta = Quill.import("delta");
const TEXT_PLACEHOLDER = "-";

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: "rich-text-editor",
  templateUrl: "./rich-text-editor.component.html",
  styleUrls: ["./rich-text-editor.component.less"],
})
/**
 * @example <rich-text-editor [onCommentPosted)="doSomething($event)"></rich-text-editor>
 * @example <rich-text-editor [(text)]="externalVariable" [hidePostButton]="true"></rich-text-editor>
 * @example <rich-text-editor [text]="externalVariable" [contentOnly]="true"></rich-text-editor>
 *
 * @param targetId string - targetId for comment
 * @param targetType CommentTargetType - targetType for comment
 * @param targetTitle string - targetTitle for comment
 * @param targetDate string - the target date for the comment (needed for KPI projections' and snapshots' comments)
 * @param hidePostButton boolean (default: default) - Hides the post comment button, e.g. when used in inline mode
 * @param withoutGifs boolean (default: default) - Removes the GIF selector
 * @param inline boolean (default: default) - Hide the toolbar and border unless focused
 * @param hideToolbarWhenUnfocused boolean (default: default) - Hide the toolbar unless focused
 * @param readOnly boolean (default: false) - Disable editing
 * @param contentOnly boolean (default: false) - Only show the content without toolbar, editing or placeholder
 * @param clearTextAfterPost boolean (default: true) - After posting the comment, should the text field be cleared
 * @param focusOnEditable boolean (default: true) - Should field be focused when contentOnly switches from true to false, e.g. when editing comments
 * @param commentId string - If populated the post button will update the comment with the given ID instead of inserting a new comment
 * @param text string (default: "") - Text to be populated in the editor
 * @param placeholder string - Placeholder for the editor when no text is input
 * @param gif IGif - Gif to be populated in the editor
 * @param textChange EventEmitter<string>
 * @param markdownChange EventEmitter<string> - Temporary until we scrap markdown all in favor of rich text
 * @param gifChange EventEmitter<gif>
 * @param mentionsChange EventEmitter<string[]
 * @param commentPosted EventEmitter<Comments>
 * @param onBlur EventEmitter<void>
 * @param onCancel new EventEmitter<void> - used to indicate whether the cancel button was clicked
 * @param a11yRequired notifies assistive technologies whether field is required
 * @param a11yLabel notifies assistive technologies the label of the field
 * @param a11yLabelledby notifies assistive technologies the label of the field, referencing another element
 * @param ellipsis boolean (default: false) determines if the rich text editior's content should be truncated on blur
 */
export class RichTextEditorComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() public targetId: string;
  @Input() public targetType: CommentTargetType;
  @Input() public targetTitle: string;
  @Input() public targetDate: string;
  @Input() public hidePostButton: boolean;
  @Input() public showExternalEditorButtons: boolean;
  @Input() public withoutGifs: boolean;
  @Input() public inline: boolean;
  @Input() public inlineWithBorder = false;
  @Input() public hideToolbarWhenUnfocused: boolean;
  @Input() public readOnly: boolean;
  @Input() public contentOnly: boolean;
  @Input() public clearTextAfterPost = true;
  @Input() public focusOnEditable = true;
  @Input() public commentId: string;
  @Input() public text = "";
  @Input() public placeholder: string;
  @Input() public gif: IGif;
  @Input() public metadata: { [key: string]: string };
  @Input() public additionalMentions: string[] = [];
  @Input() public autofocus = false;
  @Input() public focusMe = false;
  @Input() public a11yLabel: string;
  @Input() public a11yLabelledby: string;
  @Input() public a11yRequired = false;
  @Input() public ellipsis = false;
  @Output() public readonly textChange = new EventEmitter<string>();
  @Output() public readonly markdownChange = new EventEmitter<string>();
  @Output() public readonly gifChange = new EventEmitter<IGif>();
  @Output() public readonly mentionsChange = new EventEmitter<string[]>();
  @Output() public readonly commentPosted = new EventEmitter<Comment>();
  @Output() public readonly commentEdited = new EventEmitter<Comment>();
  @Output() public readonly editorBlur = new EventEmitter<void>();
  @Output() public readonly editorFocus = new EventEmitter<void>();
  @Output() public readonly commentCancel = new EventEmitter<void>();

  @ViewChild(QuillEditorComponent, { static: true }) public editor: QuillEditorComponent;
  @ViewChild("richTextEditor") public element: ElementRef<HTMLElement>;

  public modules: QuillModules;
  public sizeOfGif = SizeOfGif;
  public isFocused = false;
  public hasContentChangedPriorToBlur = false;
  public mentionPopoverUser: Assignee | UnknownAssignee;
  public mentionElRef: ElementRef;
  public mentions: string[] = [];
  public icons = RichTextEditorIconEnum;
  public richText = "";
  public markdownText = "";
  public placeholderToShow = localize("message");

  private gifChanges = {
    isDeleted: false,
    isAdded: false,
  };
  private textCursorPositionBeforeOpeningEmojiPopup: { index: number; length: number };
  private areMentionsOpened = false;

  private removeMentionClickedListener: () => void;
  private removeMentionDeletedListener: () => void;
  private removeMentionAttachedListener: () => void;

  constructor(
    private commentsFacade: CommentsFacade,
    private destroyService$: DestroyService,
    private searchService: SearchService,
    private markedJsService: MarkedJsService,
    private renderer: Renderer2,
    private zone: NgZone,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.markedJsService.loadCustomExtensionsForRichTextEditor();

    this.setModules();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.placeholderToShow = changes.contentOnly?.currentValue ? "" : this.placeholder || localize("message");

    const isContentOnlyChangedOff = changes.contentOnly?.previousValue && !changes.contentOnly?.currentValue;

    if (this.focusOnEditable && isContentOnlyChangedOff) {
      this.focusInput({ withTimeout: true });
    }

    this.refocusOnChange(changes);

    const isTextChanged =
      (changes.text?.firstChange || changes.text?.previousValue !== changes.text?.currentValue) &&
      !!changes.text?.currentValue &&
      changes.text?.currentValue !== this.markdownText;
    if (isTextChanged) {
      this.richText = convertMarkdownToRichText(changes.text.currentValue);
    }
  }

  public ngAfterViewInit(): void {
    this.attachMentionListeners();

    if (this.autofocus) {
      this.focusInput({ withTimeout: true });
    }
  }

  public ngOnDestroy(): void {
    this.removeMentionClickedListener?.();
    this.removeMentionDeletedListener?.();
    this.removeMentionAttachedListener?.();
  }

  public postComment(): void {
    if (!this.canPostComment()) {
      return;
    }

    // Remove trailing empty lines
    const newLinePattern = /(<p><br><\/p>)+$/i;
    this.richText = this.richText?.replace(newLinePattern, "");

    const allMentions = [...new Set(this.mentions.concat(this.additionalMentions))];
    if (this.commentId) {
      this.commentsFacade
        .edit$(
          this.commentId,
          {
            text: this.markdownText || this.text || "",
            mentioned: allMentions,
            gif: this.gif ?? { id: "", searchQuery: "" }, // TODO the gifChanges seem to be completely ignored and the reason GIFs are "delete" is because they're replaced with "blanks"
          },
          this.gifChanges,
          this.metadata
        )
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            this.commentEdited.emit(res);
            this.blurHandler();
          },
          error: (error) => {
            this.uiErrorHandlingService.handleModal(createErrorObject(error));
          },
        });
    } else {
      this.commentsFacade
        .postComment$(this.newComment(allMentions), this.metadata)
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            this.commentPosted.emit(res);
          },
          error: (error) => {
            this.uiErrorHandlingService.handleModal(createErrorObject(error));
          },
        });
    }

    if (this.clearTextAfterPost) {
      this.clearComment();
    }
  }

  private newComment(mentioned: string[]): NewComment {
    const targetDate: Pick<NewComment, "targetDate"> = this.targetDate ? { targetDate: this.targetDate } : {};

    return {
      targetId: this.targetId,
      targetType: this.targetType,
      targetTitle: this.targetTitle,
      text: this.markdownText,
      mentioned,
      gif: this.gif,
      ...targetDate,
    };
  }

  private clearComment(): void {
    this.richText = "";
    this.markdownText = "";
    this.gif = null;
  }

  public cancel(): void {
    if (this.commentId) {
      this.blurHandler();
    } else {
      this.clearComment();
    }
    this.commentCancel.emit();
  }

  public canPostComment(): boolean {
    return ((!!this.markdownText || !!this.text) && this.richText !== null) || !!this.gif?.id;
  }

  public selectGif(gif: IGif): void {
    this.gif = gif;

    if (this.commentId) {
      this.gifChanges = {
        isAdded: true,
        isDeleted: false,
      };
    }

    this.gifChange.emit(gif);
  }

  public removeGif(): void {
    this.gif = null;

    if (this.commentId) {
      this.gifChanges = {
        isAdded: false,
        isDeleted: true,
      };
    }

    this.gifChange.emit(null);
  }

  public onEmojiPickerCreation(): void {
    this.focusInput();
  }

  public addEmoji(emoji: string): void {
    this.addEmojiToQuillEditor(emoji);
  }

  public showMentionMenu(): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;

    const mention = quillEditor.getModule("mention");
    mention.openMenu("@");

    // Fixes cursor reset bug on openMenu call
    const cursorPosition = quillEditor.getSelection(true)?.index;
    setTimeout(() => quillEditor.setSelection(cursorPosition, 0));
  }

  public popupMenuOpen(): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;
    this.textCursorPositionBeforeOpeningEmojiPopup = quillEditor.getSelection();

    // Fallback if currently there is no selection.
    // This is needed for example when editor is not focused and emoji picker is opened.
    // In this case if there is no selection and you select an emoji, it will not be inserted to the editor.
    if (!this.textCursorPositionBeforeOpeningEmojiPopup) {
      quillEditor.setSelection(quillEditor.getLength(), 0);
      this.textCursorPositionBeforeOpeningEmojiPopup = quillEditor.getSelection();
    }

    if (this.areMentionsOpened) {
      quillEditor.deleteText(this.textCursorPositionBeforeOpeningEmojiPopup.index - 1, 1, Quill.sources.USER);
    }
  }

  public focusHandler(): void {
    if (!this.readOnly && !this.contentOnly) {
      this.isFocused = true;
      this.editorFocus.emit();
    }
  }

  public toolBarHandler($event: MouseEvent): void {
    // (GVS-27436) excluding mousedown preventDefault for search fields otherwise they could't be focused
    const eventTargetElement = $event.target as HTMLElement;

    if (isGifOrEmojiPickerSearchFieldSelected(eventTargetElement)) {
      return;
    }

    // we are trapping the mousedown on the toolbar to prevent a blur event when clicked
    $event.preventDefault();
  }

  public blurHandler(): void {
    if (this.text === TEXT_PLACEHOLDER) {
      this.editor.quillEditor.deleteText(0, 1);
    }
    const activeElement = document.activeElement as HTMLElement;

    // On Safari the reader blur event is triggered when focus moves to the toolbar buttons
    if (this.element?.nativeElement?.contains(activeElement)) {
      return;
    }

    if (!this.isFocused || isGifOrEmojiPickerSearchFieldSelected(activeElement)) {
      return;
    }

    if (!this.readOnly && !this.contentOnly) {
      this.isFocused = false;
      this.editorBlur.emit();

      if (this.hasContentChangedPriorToBlur) {
        // Text gets recognized as link, when converted from MD to HTML
        this.richText = convertMarkdownToRichText(this.markdownText);
      }
    }
  }

  public mentionPopoverClosed(): void {
    this.mentionPopoverUser = null;
    this.mentionElRef = null;
  }

  public onContentChanged(): void {
    const { quillEditor } = this.editor;

    const markdown = deltaToMarkdown(quillEditor.getContents().ops, deltaToMarkdownConverters()).trim();
    const stripped = stripHtmlElements(markdown);

    this.markdownText = stripped;

    this.textChange.emit(stripped);
    this.hasContentChangedPriorToBlur = true;
  }

  public onEditorCreated(): void {
    const keyboard = this.editor.quillEditor.getModule("keyboard");
    delete keyboard.bindings[9];

    // we need this blur event instead of onBlur because quill editor by their implementation recognises
    // blur event that are outside editor and are not buttons or inputs https://github.com/KillerCodeMonkey/ngx-quill/issues/390
    // BUG https://quantive-inc.atlassian.net/browse/GVS-41652
    fromEvent(this.editor.quillEditor.root, "blur")
      .pipe(takeUntil(this.destroyService$))
      .subscribe(() => {
        // blur must be delayed to prevent the editor from losing focus when clicking on the toolbar
        // For example when editing links
        // BUG - https://quantive-inc.atlassian.net/browse/GVS-37024
        window.setTimeoutOutsideAngular(() => this.blurHandler());
      });
  }

  private focusInput(options = { withTimeout: false }): void {
    const focusRTE = (): void => {
      const innerEditor = this.editor?.editorElem?.firstChild as HTMLElement;
      innerEditor?.focus();

      innerEditor && this.setCursorPositionInTheEnd(innerEditor);

      this.isFocused = true;
    };

    if (options.withTimeout === true) {
      // Small timeout is needed for the focus event to work
      window.setTimeoutOutsideAngular(() => {
        focusRTE();
      }, 50);

      return;
    }

    focusRTE();
  }

  private setCursorPositionInTheEnd = (innerEditor: HTMLElement): void => {
    const lastChildNode = this.getLastChildNode(innerEditor.lastChild);

    const range = document.createRange();
    const selection = window.getSelection();

    range.setStart(lastChildNode, lastChildNode.nodeValue?.length || 0);
    range.collapse(true);

    selection.removeAllRanges();
    selection.addRange(range);
  };

  private getLastChildNode = (node: ChildNode): ChildNode => {
    if (!node.lastChild) {
      return node;
    }

    return this.getLastChildNode(node.lastChild);
  };

  private onImgHandler(): void {
    return new Delta().insert("");
  }

  private setModules(): void {
    const dataAttributes: (keyof UserAssignee)[] = ["id", "name", "email", "picture"];

    this.modules = {
      mention: {
        blotName: "ghMention",
        positioningStrategy: "fixed",
        dataAttributes,
        // default is /^[a-zA-Z0-9_]*$/
        allowedChars: /^[a-zA-ZÀ-ÖÙ-öù-ÿĀ-ž\u1e00-\u1eff0-9_]*$/,
        renderItem: (item: UserAssignee): string => {
          return this.renderMentionItem(item);
        },
        onSelect: (item: UserAssignee, insertItem: (item: UserAssignee) => void): void => {
          insertItem(item);

          this.addMention(item.id);

          // Necessary because quill-mention triggers changes as 'api' instead of 'user'
          this.addTextToQuillEditor("");
        },
        onOpen: (): void => {
          this.areMentionsOpened = true;
        },
        onClose: (): void => {
          this.areMentionsOpened = false;
        },
        source: async (searchTerm: string, renderList: (items: UserAssignee[], searchTerm: string) => void): Promise<void> => {
          const searchConditions: ISearchCondition[] = [
            {
              fieldName: "isActive",
              fieldCondition: {
                operator: "isNot",
                value: "false",
              },
            },
          ];

          const searchBody: SearchDTO = {
            searchRequests: [
              {
                collectionName: "users",
                ...(searchTerm && {
                  searchFields: [{ name: "firstName" }, { name: "lastName" }, { name: "fullName" }],
                  responseFields: ["_id", "firstName", "lastName", "auth0Cache.email", "auth0Cache.picture"],
                }),
                searchConditions,
              },
            ],
            ...(searchTerm && {
              searchTerm,
              operator: SearchOperatorsEnum.matchPrefix,
            }),
          };
          const queryParams: Partial<ISearchParams> = { take: 10, skip: 0 };
          const searchResults = await this.searchService.getSomeSearchData<Search<"users">>(searchBody, queryParams);

          const renderListValues: UserAssignee[] = [];
          searchResults.items.forEach((item) => {
            const user = searchItemToUserAssignee(item);
            renderListValues.push(user);
          });

          renderList(renderListValues, searchTerm);
        },
      },
      keyboard: {
        bindings: {
          enter: {
            key: 13,
            shortKey: true,
            handler: (): void => {
              if (this.hidePostButton || !this.canPostComment()) {
                return;
              }

              this.postComment();
            },
          },
        },
      },
      clipboard: {
        matchers: [
          ["IMG", this.onImgHandler],
          ["PICTURE", this.onImgHandler],
        ],
      },
    };
  }

  private addTextToQuillEditor(text: string): void {
    if (!this.editor) {
      return;
    }

    const { quillEditor } = this.editor;
    const idx = quillEditor.getLength() - 1;

    quillEditor.insertText(idx, text, Quill.sources.USER);
  }

  private addEmojiToQuillEditor(text: string): void {
    if (!this.editor || !this.textCursorPositionBeforeOpeningEmojiPopup) {
      return;
    }

    const { quillEditor } = this.editor;

    if (this.textCursorPositionBeforeOpeningEmojiPopup.length > 0) {
      quillEditor.deleteText(this.textCursorPositionBeforeOpeningEmojiPopup.index, this.textCursorPositionBeforeOpeningEmojiPopup.length);
    }

    quillEditor.insertEmbed(this.textCursorPositionBeforeOpeningEmojiPopup.index, EmbeddedEmoji.blotName, text.trim(), Quill.sources.USER);
    quillEditor.setSelection(this.textCursorPositionBeforeOpeningEmojiPopup.index + 1, 0, Quill.sources.USER);
  }

  private addMention(userId: string): void {
    if (userId === getCurrentUserId()) {
      return;
    }

    this.mentions.push(userId);
    this.mentions = [...new Set(this.mentions)];
    this.mentionsChange.emit(this.mentions);
  }

  private removeMention(userId: string): void {
    this.mentions = this.mentions.filter((it) => it !== userId);

    if (this.mentionsChange.observed) {
      this.zone.run(() => this.mentionsChange.emit(this.mentions));
    }
  }

  private renderMentionItem(item: UserAssignee): string {
    const wrapper = document.createElement("div");

    const pictureEl = document.createElement("span");
    pictureEl.classList.add("mention-item-avatar");
    const imgEl = document.createElement("img");
    imgEl.src = item.picture || "/dist/img/icons/unknown.svg";
    imgEl.alt = item.name;
    pictureEl.appendChild(imgEl);
    wrapper.appendChild(pictureEl);

    const nameEl = document.createElement("span");
    nameEl.classList.add("mention-item-content");
    nameEl.textContent = item.name;

    wrapper.appendChild(nameEl);

    return wrapper.innerHTML;
  }

  private setMentionPopoverUser(userId: string): void {
    const state: IAssigneesStoreState = reduxStoreContainer.reduxStore.getState();
    this.mentionPopoverUser = assigneeFromRedux(state, userId);
  }

  private attachMentionListeners(): void {
    this.zone.runOutsideAngular(() => {
      this.removeMentionClickedListener = this.renderer.listen(this.element.nativeElement, "click", (event) => {
        const mentionElement = event.target?.closest(".mention");

        if (mentionElement) {
          this.zone.run(() => {
            this.mentionElRef = new ElementRef(mentionElement);
            this.setMentionPopoverUser(mentionElement.getAttribute("data-id"));
          });
        }
      });

      this.removeMentionAttachedListener = this.renderer.listen(this.element.nativeElement, "mention-attached", (event: MentionAttachedEvent) => {
        this.addMention(event.userId);
      });

      this.removeMentionDeletedListener = this.renderer.listen(this.element.nativeElement, "mention-deleted", (event: MentionDeletedEvent) => {
        if (this.isFocused) {
          this.removeMention(event.userId);
        }
      });
    });
  }

  private refocusOnChange(changes: SimpleChanges): void {
    if (changes?.focusMe?.currentValue) this.focusInput({ withTimeout: true });
  }
}
